import jsPDF from 'jspdf';
import '../content/WorkSans';
import '../content/WorkSans-light';
import '../content/WorkSans-bold';

const GLOBAL_FONT = 'WorkSans'; // Define the global font name
const GLOBAL_NAMEDATE_WEIGHT = 'normal';
const GLOBAL_TITLE_WEIGHT = 'bold';
const GLOBAL_WORDBANK_WEIGHT = 'normal';
const GLOBAL_ANSWERS_WEIGHT = 'bold';
const GLOBAL_ANSWERS_GRIDTEXT_WEIGHT = 'light';
const GLOBAL_GRIDTEXT_WEIGHT = 'normal';

export const generatePDF = (grid, placedWords, title) => {
  const pdf = new jsPDF();
  const gridSize = grid.length;
  const cellSize = 10;
  const pageWidth = pdf.internal.pageSize.width;
  const margin = 20; // 1-inch margin
  const gridStartX = (pageWidth - gridSize * cellSize) / 2;
  const startY = 60; // Moved grid higher by reducing starting Y position

  // Name and Date
  pdf.setFont(GLOBAL_FONT, GLOBAL_NAMEDATE_WEIGHT);
  pdf.setFontSize(12);
  pdf.text('Name: ____________________', margin, 20);
  pdf.text('Date: ____________________', pageWidth - margin - 60, 20);

  // Title
  pdf.setFont(GLOBAL_FONT, GLOBAL_TITLE_WEIGHT);
  pdf.setFontSize(22); // Larger title font
  pdf.text(title, pageWidth / 2, 45, { align: 'center' }); // Adjusted Y position of the title

  // Draw the grid (no visible borders)
  pdf.setFont(GLOBAL_FONT, GLOBAL_GRIDTEXT_WEIGHT);
  pdf.setFontSize(14);
  for (let row = 0; row < gridSize; row++) {
    for (let col = 0; col < gridSize; col++) {
      const x = gridStartX + col * cellSize;
      const y = startY + row * cellSize;
      const letter = grid[row][col] || '';
      const textX = x + cellSize / 2;
      const textY = y + cellSize / 2 + 1.25;
      pdf.text(letter, textX, textY, { align: 'center' });
    }
  }

  // Word Bank
  const wordBankStartY = pdf.internal.pageSize.height - margin; // Positioned at the bottom margin
  const wordBankTextSize = 14; // Larger text for the word bank
  const columnSpacing = 20; // Space between columns
  const totalColumnWidth = 80; // Combined width of both columns
  const columnWidth = totalColumnWidth / 2;
  const centerX = pageWidth / 2 + 20; // Adjusted center point to the right by 5 units

  pdf.setFont(GLOBAL_FONT, GLOBAL_WORDBANK_WEIGHT);
  pdf.setFontSize(wordBankTextSize);

  // Create two columns for the word bank
  const half = Math.ceil(placedWords.length / 2);
  const column1 = placedWords.slice(0, half).map((wordObj) => wordObj.word);
  const column2 = placedWords.slice(half).map((wordObj) => wordObj.word);

  // Draw columns
  const columnStartX1 = centerX - columnWidth - columnSpacing / 2; // Left column
  const columnStartX2 = centerX + columnSpacing / 2; // Right column

  column1.forEach((word, index) => {
    const y = wordBankStartY - (column1.length - index) * 8; // Adjust row spacing
    pdf.text(word, columnStartX1, y, { align: 'center' });
  });

  column2.forEach((word, index) => {
    const y = wordBankStartY - (column2.length - index) * 8; // Adjust row spacing
    pdf.text(word, columnStartX2, y, { align: 'center' });
  });

  const sanitizedTitle = title.replace(/[^a-zA-Z0-9]/g, '_');
  pdf.save(`${sanitizedTitle}.pdf`);
};

export const generateAnswerKeyPDF = (grid, placedWords, title) => {
  const pdf = new jsPDF();
  const gridSize = grid.length;
  const cellSize = 10; // Each cell's size in the grid
  const pageWidth = pdf.internal.pageSize.width;
  const margin = 20; // 1-inch margin
  const gridStartX = (pageWidth - gridSize * cellSize) / 2;
  const gridStartY = 50; // Top margin for the grid

  // Title
  const fullTitle = `${title} Answer Key`;
  pdf.setFont(GLOBAL_FONT, GLOBAL_TITLE_WEIGHT);
  pdf.setFontSize(22); // Larger title font
  pdf.text(fullTitle, pageWidth / 2, 30, { align: 'center' });

  // Draw the grid with answers
  pdf.setFontSize(14);
  for (let row = 0; row < gridSize; row++) {
    for (let col = 0; col < gridSize; col++) {
      const x = gridStartX + col * cellSize;
      const y = gridStartY + row * cellSize;
      const letter = grid[row][col] || '';
      const textX = x + cellSize / 2;
      const textY = y + cellSize / 2 + 1.25;

      const isAnswer = placedWords.some((word) =>
        word.positions.some((pos) => pos.x === col && pos.y === row)
      );

      pdf.setFont(GLOBAL_FONT, isAnswer ? GLOBAL_ANSWERS_WEIGHT : GLOBAL_GRIDTEXT_WEIGHT);
      pdf.text(letter, textX, textY, { align: 'center' });
    }
  }

  // Draw thin lines through words
  pdf.setDrawColor(255, 0, 0); // Red for visibility
  pdf.setLineWidth(0.25); // Thin line

  placedWords.forEach(({ positions }) => {
    if (positions.length > 1) {
      const first = positions[0];
      const last = positions[positions.length - 1];

      const startX = gridStartX + first.x * cellSize + cellSize / 2;
      const startY = gridStartY + first.y * cellSize + cellSize / 2;
      const endX = gridStartX + last.x * cellSize + cellSize / 2;
      const endY = gridStartY + last.y * cellSize + cellSize / 2;

      // Draw the line
      pdf.line(startX, startY, endX, endY);
    }
  });

  // Word Bank
  const wordBankStartY = pdf.internal.pageSize.height - margin - 5; // Positioned at the bottom margin
  const wordBankTextSize = 14;
  const columnSpacing = 20; // Space between columns
  const totalColumnWidth = 80; // Combined width of both columns
  const columnWidth = totalColumnWidth / 2;
  const centerX = pageWidth / 2 + 20;

  pdf.setFont(GLOBAL_FONT, GLOBAL_WORDBANK_WEIGHT);
  pdf.setFontSize(wordBankTextSize);

  const half = Math.ceil(placedWords.length / 2);
  const column1 = placedWords.slice(0, half).map((wordObj) => wordObj.word);
  const column2 = placedWords.slice(half).map((wordObj) => wordObj.word);

  // Draw columns
  const columnStartX1 = centerX - columnWidth - columnSpacing / 2; // Left column
  const columnStartX2 = centerX + columnSpacing / 2; // Right column

  column1.forEach((word, index) => {
    const y = wordBankStartY - (column1.length - index) * 8; // Adjust row spacing
    pdf.text(word, columnStartX1, y, { align: 'center' });
  });

  column2.forEach((word, index) => {
    const y = wordBankStartY - (column2.length - index) * 8; // Adjust row spacing
    pdf.text(word, columnStartX2, y, { align: 'center' });
  });

  const sanitizedTitle = fullTitle.replace(/[^a-zA-Z0-9]/g, '_');
  pdf.save(`${sanitizedTitle}.pdf`);
};



// export const generateAnswerKeyPDF = (grid, placedWords, title) => {
//   const pdf = new jsPDF();
//   const gridSize = grid.length;
//   const cellSize = 10;
//   const pageWidth = pdf.internal.pageSize.width;
//   const margin = 20; // 1-inch margin
//   const gridStartX = (pageWidth - gridSize * cellSize) / 2;
//   const startY = 50; // Moved grid higher by reducing starting Y position

//   // Title
//   const fullTitle = `${title} Answer Key`;
//   pdf.setFont(GLOBAL_FONT, GLOBAL_TITLE_WEIGHT);
//   pdf.setFontSize(22); // Larger title font
//   pdf.text(fullTitle, pageWidth / 2, 30, { align: 'center' }); // Adjusted Y position of the title

//   // Draw the grid with bold answers
//   pdf.setFontSize(14);
//   for (let row = 0; row < gridSize; row++) {
//     for (let col = 0; col < gridSize; col++) {
//       const x = gridStartX + col * cellSize;
//       const y = startY + row * cellSize;
//       const letter = grid[row][col] || '';
//       const textX = x + cellSize / 2;
//       const textY = y + cellSize / 2 + 1.25;

//       // Check if the letter is part of an answer
//       const isAnswer = placedWords.some((word) =>
//         word.positions.some((pos) => pos.x === col && pos.y === row)
//       );

//       pdf.setFont(GLOBAL_FONT, isAnswer ? GLOBAL_ANSWERS_WEIGHT : GLOBAL_ANSWERS_GRIDTEXT_WEIGHT);
//       pdf.text(letter, textX, textY, { align: 'center' });
//     }
//   }

//   // Word Bank
//   const wordBankStartY = pdf.internal.pageSize.height - margin - 5; // Positioned at the bottom margin
//   const wordBankTextSize = 14; // Larger text for the word bank
//   const columnSpacing = 20; // Space between columns
//   const totalColumnWidth = 80; // Combined width of both columns
//   const columnWidth = totalColumnWidth / 2;
//   const centerX = pageWidth / 2 + 20; // Adjusted center point to the right by 5 units

//   pdf.setFont(GLOBAL_FONT, GLOBAL_WORDBANK_WEIGHT);
//   pdf.setFontSize(wordBankTextSize);

//   const half = Math.ceil(placedWords.length / 2);
//   const column1 = placedWords.slice(0, half).map((wordObj) => wordObj.word);
//   const column2 = placedWords.slice(half).map((wordObj) => wordObj.word);

//   // Draw columns
//   const columnStartX1 = centerX - columnWidth - columnSpacing / 2; // Left column
//   const columnStartX2 = centerX + columnSpacing / 2; // Right column

//   column1.forEach((word, index) => {
//     const y = wordBankStartY - (column1.length - index) * 8; // Adjust row spacing
//     pdf.text(word, columnStartX1, y, { align: 'center' });
//   });

//   column2.forEach((word, index) => {
//     const y = wordBankStartY - (column2.length - index) * 8; // Adjust row spacing
//     pdf.text(word, columnStartX2, y, { align: 'center' });
//   });

//   const sanitizedTitle = fullTitle.replace(/[^a-zA-Z0-9]/g, '_');
//   pdf.save(`${sanitizedTitle}.pdf`);
// };
